import React, {
	useEffect,
	useReducer,
	useContext,
	createContext,
	Context,
} from 'react'

import { getMarket } from 'shared/index'
import Multistep, { IMultistepComponent } from 'shared/components/Multistep'

import { Heading } from 'selfServicePortal/components/index'
import { SDDCustomerInformationForm } from './MultistepBlocks/SDDCustomerInformationForm'
import { SDDTermsAndConditions } from './MultistepBlocks/SDDTermsAndConditions'
import { SDDAuthentication } from './MultistepBlocks/SDDAuthentication'
import { SDDSuccessResponse } from './MultistepBlocks/SDDSuccessResponse'

import {
	OtpDistributionChannel,
	SddWithdrawalType,
	Market,
	IBankInfoProps,
} from 'selfServicePortal/interfaces/i-api-sdd'
import SspContext from 'selfServicePortal/contexts/SspContext'

interface ISddProps {
	market: Market
	customerId: string
	title: string
	firstName: string
	lastName: string
	street: string
	houseNumber: string
	postalCode: string
	city: string
	emailAddress: string
	phoneNumber: string
	debtorIban: string
	debtorBic: string
	sddWithdrawalType: SddWithdrawalType
	withdrawalAmount: number
	otpCode: string
	distributionChannel: OtpDistributionChannel
	otpDistributedToContactInfo: string
	sessionId: string
	expiryAtUtc: string
	mandateDocumentId: string | undefined
	approvedTerms: boolean
	authSelect: boolean
	description: string
	bankInfoList: IBankInfoProps[]
}

const initialState: ISddProps = {
	market: Market.Global,
	customerId: '',
	title: '',
	firstName: '',
	lastName: '',
	street: '',
	houseNumber: '',
	postalCode: '',
	city: '',
	emailAddress: '',
	phoneNumber: '',
	debtorIban: '',
	debtorBic: '',
	sddWithdrawalType: SddWithdrawalType.Minimum,
	withdrawalAmount: 0,
	otpCode: '',
	distributionChannel: OtpDistributionChannel.Unknown,
	otpDistributedToContactInfo: '',
	sessionId: '',
	expiryAtUtc: '',
	mandateDocumentId: undefined,
	approvedTerms: false,
	authSelect: true,
	description: '',
	bankInfoList: [],
}

export const SDD_ACTIONS = {
	SET_DESCRIPTION: 'SET_DESCRIPTION',
	SET_CUSTOMER_INFO: 'SET_CUSTOMER_INFO',
	SET_BANK_INFO_LIST: 'SET_BANK_INFO_LIST:',
	SET_CUSTOMER_INFO_COMPLETE: 'SET_CUSTOMER_COMPLETE',
	SET_BANK_INFO: 'SET_BANK_INFO',
	SET_APPROVE_TERMS: 'SET_APPROVE_TERMS',
	SET_DISTRIBUTION_CHANNEL: 'SET_DISTRIBUTION_CHANNEL',
	SET_SESSION_ID: 'SET_SESSION_ID',
	SET_OTP_INFO: 'SET_OTP_INFO',
	SET_AUTH_SELECT: 'SET_AUTH_SELECT',
	SET_MANDATE_DOC: 'SET_MANDATE_DOC',
}

function reducer(sdd: any, action: any) {
	switch (action.type) {
		case SDD_ACTIONS.SET_DESCRIPTION:
			return {
				...sdd,
				description: action.payload.description,
			}
		case SDD_ACTIONS.SET_CUSTOMER_INFO:
			return {
				...sdd,
				market: action.payload.market,
				customerId: action.payload.customerId,
				title: action.payload.title,
				firstName: action.payload.firstName,
				lastName: action.payload.lastName,
				street: action.payload.street,
				houseNumber: action.payload.houseNumber,
				postalCode: action.payload.postalCode,
				city: action.payload.city,
				emailAddress: action.payload.emailAddress,
				phoneNumber: action.payload.phoneNumber,
				bankInfoList: action.payload.bankInfoList,
			}
		case SDD_ACTIONS.SET_BANK_INFO:
			return {
				...sdd,
				debtorIban: action.payload.debtorIban,
				debtorBic: action.payload.debtorBic,
				sddWithdrawalType: action.payload.sddWithdrawalType,
				withdrawalAmount: action.payload.withdrawalAmount,
			}
		case SDD_ACTIONS.SET_BANK_INFO_LIST:
			return {
				...sdd,
				bankInfoList: action.payload.bankInfoList,
			}
		case SDD_ACTIONS.SET_APPROVE_TERMS:
			return {
				...sdd,
				approvedTerms: action.payload.approvedTerms,
			}
		case SDD_ACTIONS.SET_DISTRIBUTION_CHANNEL:
			return {
				...sdd,
				distributionChannel: action.payload.distributionChannel,
			}
		case SDD_ACTIONS.SET_AUTH_SELECT:
			return {
				...sdd,
				authSelect: action.payload.authSelect,
			}
		case SDD_ACTIONS.SET_OTP_INFO:
			return {
				...sdd,
				otpDistributedToContactInfo:
					action.payload.otpDistributedToContactInfo,
				sessionId: action.payload.sessionId,
				expiryAtUtc: action.payload.expiryAtUtc,
			}
		case SDD_ACTIONS.SET_MANDATE_DOC:
			return {
				...sdd,
				mandateDocumentId: action.payload.mandateDocumentId,
			}
		default:
			return sdd
	}
}

export const SddDispatchContext: Context<any> = createContext(initialState)

interface IProps {
	bankInfoList: IBankInfoProps[]
}

export const SddMultistepValues = ({ bankInfoList }: IProps) => {
	const [sdd, dispatchSdd] = useReducer(reducer, initialState)
	const { customerData } = useContext(SspContext)

	useEffect(() => {
		dispatchSdd({
			type: SDD_ACTIONS.SET_CUSTOMER_INFO,
			payload: {
				market: getMarket(),
				customerId: customerData!.customerId,
				title: customerData?.title,
				firstName: customerData?.firstName,
				lastName: customerData?.lastName,
				street: customerData?.street,
				houseNumber: customerData?.houseNumber,
				postalCode: customerData?.postalCode,
				city: customerData?.city,
				emailAddress: customerData?.email,
				phoneNumber: customerData?.mobilePhoneNumber,
				bankInfoList: bankInfoList,
			},
		})
	}, [bankInfoList, customerData])

	const multistepValues: IMultistepComponent[] = [
		{
			id: 1,
			header: 'SDDCustomerInformationForm',
			body: <SDDCustomerInformationForm />,
			show: true,
			stepCompleted: false,
		},
		{
			id: 2,
			header: 'SDDTermsAndConditions',
			body: <SDDTermsAndConditions />,
			show: true,
			stepCompleted: sdd.approvedTerms,
		},
		{
			id: 3,
			header: 'SDDAuthentication',
			body: <SDDAuthentication />,
			show: true,
			stepCompleted: sdd.authComplete,
		},
		{
			id: 4,
			header: 'SDDSuccessResponse',
			body: <SDDSuccessResponse />,
			show: true,
			stepCompleted: false,
		},
	]

	return (
		<SddDispatchContext.Provider value={[sdd, dispatchSdd]}>
			<Heading title="SDDTitle" description={sdd.description} />
			<Multistep multisteps={multistepValues} resetInitialState={true} />
		</SddDispatchContext.Provider>
	)
}
